/**
 * useScannerRequestCoordinator
 *
 * Deterministic completion tracker for in-flight scanner requests.
 *
 * Problem: an HTTP response and an SSE event can arrive in any order.
 * This coordinator ensures the UI settles exactly once per scan request,
 * using `clientCorrelationId` as the key — never "last response wins".
 *
 * Responsibilities:
 * - Register each scan request via `begin(correlationId)` → returns a Promise
 * - Resolve the Promise when the matching SSE event arrives (`resolveFromSse`)
 * - Reject (fail) the Promise on HTTP error (`failFromHttp`)
 * - Apply a configurable timeout that marks the request as `Failed`
 *
 * Usage:
 *   const coordinator = useScannerRequestCoordinator();
 *
 *   const correlationId = crypto.randomUUID();
 *   const completion = coordinator.begin(correlationId);
 *
 *   // ... fire HTTP request, handle errors via failFromHttp ...
 *
 *   // SSE handler:
 *   coordinator.resolveFromSse(event.clientCorrelationId, event);
 *
 *   const result = await completion; // resolves/rejects when SSE arrives or timeout fires
 */
import { ref } from 'vue';

// ── Types ──────────────────────────────────────────────────────────────────

/** Possible states for a scanner request visible to the UI. */
export type ScannerUiStatus = 'Idle' | 'Scanning' | 'Decoded' | 'NoBarcode' | 'ScannerBusy' | 'Failed';

type Deferred<T> = {
  resolve: (value: T) => void;
  reject: (error: Error) => void;
  timeoutId: ReturnType<typeof setTimeout> | null;
  settled: boolean;
};

// ── Composable ─────────────────────────────────────────────────────────────

/** How long a settled status entry lingers in the map before being evicted. */
const STATUS_TTL_MS = 5_000;

export function useScannerRequestCoordinator(timeoutMs = 30_000) {
  /** Reactive status map — keyed by clientCorrelationId. */
  const statusByCorrelation = ref(new Map<string, ScannerUiStatus>());
  /** In-flight deferred promises — keyed by clientCorrelationId. */
  const inflight = new Map<string, Deferred<unknown>>();

  /** Remove a settled status entry after STATUS_TTL_MS so the map doesn't grow unboundedly. */
  function scheduleStatusCleanup(correlationId: string) {
    setTimeout(() => {
      statusByCorrelation.value.delete(correlationId);
    }, STATUS_TTL_MS);
  }

  /**
   * Register a new scan request.
   * Sets status to `Scanning` and returns a Promise that settles when
   * `resolveFromSse` / `failFromHttp` is called or the timeout fires.
   */
  function begin(correlationId: string): Promise<unknown> {
    statusByCorrelation.value.set(correlationId, 'Scanning');

    return new Promise((resolve, reject) => {
      const deferred: Deferred<unknown> = {
        resolve,
        reject,
        settled: false,
        timeoutId: setTimeout(() => {
          if (deferred.settled) return;
          deferred.settled = true;
          statusByCorrelation.value.set(correlationId, 'Failed');
          inflight.delete(correlationId);
          scheduleStatusCleanup(correlationId);
          reject(new Error('Scanner request timed out'));
        }, timeoutMs),
      };

      inflight.set(correlationId, deferred);
    });
  }

  /**
   * Settle the request using data from an SSE event.
   * No-op if the correlation id is unknown or already settled.
   */
  function resolveFromSse(
    correlationId: string,
    payload: { status?: string; success?: boolean; errorMessage?: string | null },
  ) {
    const deferred = inflight.get(correlationId);
    if (!deferred || deferred.settled) return;

    deferred.settled = true;
    if (deferred.timeoutId) clearTimeout(deferred.timeoutId);

    const status = mapBackendStatus(payload.status, payload.success, payload.errorMessage);
    statusByCorrelation.value.set(correlationId, status);
    inflight.delete(correlationId);
    scheduleStatusCleanup(correlationId);
    deferred.resolve(payload);
  }

  /**
   * Fail the request due to an HTTP error response.
   * No-op if the correlation id is unknown or already settled.
   */
  function failFromHttp(correlationId: string, error: Error) {
    const deferred = inflight.get(correlationId);
    if (!deferred || deferred.settled) return;

    deferred.settled = true;
    if (deferred.timeoutId) clearTimeout(deferred.timeoutId);

    statusByCorrelation.value.set(correlationId, mapHttpError(error));
    inflight.delete(correlationId);
    scheduleStatusCleanup(correlationId);
    deferred.reject(error);
  }

  // ── Status helpers ───────────────────────────────────────────────────────

  function mapBackendStatus(
    status?: string,
    success?: boolean,
    errorMessage?: string | null,
  ): ScannerUiStatus {
    if (errorMessage?.includes('ScannerBusy') || status === 'ScannerBusy') return 'ScannerBusy';
    if (status === 'NoBarcode' || status === 'NotFound') return 'NoBarcode';
    if (success) return 'Decoded';
    return 'Failed';
  }

  function mapHttpError(error: Error): ScannerUiStatus {
    if (error.message.includes('409') || error.message.includes('ScannerBusy')) return 'ScannerBusy';
    return 'Failed';
  }

  return {
    statusByCorrelation,
    begin,
    resolveFromSse,
    failFromHttp,
  };
}
